import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Mail,
  MessageSquare,
  Bell,
  Users,
  Car,
  FileText,
  Loader2,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export const Administration = () => {
  const { user, userData } = useAuth();
  const queryClient = useQueryClient();
  const [telegramChatId, setTelegramChatId] = useState('');

  if (!userData?.isAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Access Denied
          </h2>
          <p className="text-red-600">
            You do not have administrator privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const { data: statistics, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-statistics'],
    queryFn: async () => {
      if (!user) return null;
      const token = await user.getIdToken();
      const response = await axios.get(`${API_URL}/admin/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    },
    enabled: !!user && !!userData?.isAdmin,
  });

  const testEmailMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      await axios.post(
        `${API_URL}/admin/test/email`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      toast.success('Test email sent successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    },
  });

  const testTelegramMutation = useMutation({
    mutationFn: async (chatId: string) => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      await axios.post(
        `${API_URL}/admin/test/telegram`,
        { chatId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      toast.success('Test Telegram message sent successfully');
      setTelegramChatId('');
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to send test Telegram message'
      );
    },
  });

  const testRemindersMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      await axios.post(
        `${API_URL}/admin/test/reminders`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      toast.success('Reminder check completed successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-statistics'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to check reminders');
    },
  });

  const handleTestEmail = () => {
    testEmailMutation.mutate();
  };

  const handleTestTelegram = () => {
    if (!telegramChatId.trim()) {
      toast.error('Please enter a Telegram chat ID');
      return;
    }
    testTelegramMutation.mutate(telegramChatId);
  };

  const handleTestReminders = () => {
    testRemindersMutation.mutate();
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-8">Administration</h1>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4">Service Tests</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Mail className="w-6 h-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Email Test</h3>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                Send a test email to verify email notifications are working.
              </p>
              <button
                onClick={handleTestEmail}
                disabled={testEmailMutation.isPending}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {testEmailMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Test Email'
                )}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <MessageSquare className="w-6 h-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold">Telegram Test</h3>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                Send a test message to verify Telegram notifications are
                working.
              </p>
              <input
                type="text"
                value={telegramChatId}
                onChange={(e) => setTelegramChatId(e.target.value)}
                placeholder="Telegram Chat ID"
                className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleTestTelegram}
                disabled={testTelegramMutation.isPending}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {testTelegramMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Test Message'
                )}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Bell className="w-6 h-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold">Reminders Test</h3>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                Manually trigger reminder check to verify scheduled
                notifications.
              </p>
              <button
                onClick={handleTestReminders}
                disabled={testRemindersMutation.isPending}
                className="w-full bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {testRemindersMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Check Reminders'
                )}
              </button>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Statistics</h2>
          {statsLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center mb-4">
                  <Users className="w-8 h-8 text-blue-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold">Total Users</h3>
                    <p className="text-3xl font-bold text-gray-900">
                      {statistics?.totalUsers || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center mb-4">
                  <Car className="w-8 h-8 text-green-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold">Total Vehicles</h3>
                    <p className="text-3xl font-bold text-gray-900">
                      {statistics?.totalVehicles || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center mb-4">
                  <FileText className="w-8 h-8 text-purple-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold">Total Records</h3>
                    <p className="text-3xl font-bold text-gray-900">
                      {statistics?.totalRecords || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
